import { GraphUtil } from 'graphs-for-js';
import { Operator, UrlFilters } from '../../../hooks/useUrlFilters';
import { Totals, TestResult } from '../../../types/types';
import {
  debugLog,
  distinctArrayBy,
  intersectArraysBy,
} from '../../../utils/common';
import {
  EnrichedLineageNode,
  GetGraphOnMessageData,
  PostMessageData,
  ReactFlowGraph,
  WorkerData,
} from '../types';
import { getEnrichedLineageData } from './lineageApiEnrichment';
import {
  hideAndShiftGraphEdges,
  initGraph,
  highlightNode,
  getGraphNodes,
  getReactFlowGraph,
  getStreamNodes,
  getNodesOnPath,
  getDagDepthBoundary,
  applyHopLimit,
  buildRenderedEdgesFromFilteredGraph,
  checkGraphSizeLimitExceeded,
  mergeHighlightColumnSubgraphIntoWorkerData,
  reapplyLazyLoadedColumns,
} from './lineageGraphUtils';

type NodeFilterKey = keyof Omit<
  EnrichedLineageNode,
  'label' | 'children' | 'type' | 'id'
>;

export type RebuildGraphLayoutParams = {
  selectedNodes?: string[];
  highlightedNode?: string;
  expandNodes?: string[];
  collapseNodes?: string[];
  lockPosNodes?: string[];
  reactFlowGraph?: GetGraphOnMessageData['reactFlowGraph'];
  calcDownstreamUpstream?: boolean;
  isApiLineage?: boolean;
  onCycleDetected?: (cycleNodes: string[]) => void;
};

export type RebuildGraphLayoutResult = {
  reactFlowGraph: ReactFlowGraph;
  rootNodes: EnrichedLineageNode[];
};

const evaluateStringFilter = (
  value: string,
  filterValues: string[],
  operator: Operator,
) => {
  const valueFixed = value.toLowerCase();
  const filterValuesFixed = filterValues.map((v) => v.toLowerCase());

  switch (operator) {
    case 'equals':
      return filterValuesFixed.some((v) => v === valueFixed);
    case 'notEquals':
      return !filterValuesFixed.some((v) => v === valueFixed);
    case 'contains':
      return filterValuesFixed.some((v) => valueFixed.includes(v));
    case 'notContains':
      return !filterValuesFixed.some((v) => valueFixed.includes(v));
    default:
      return false;
  }
};

const getNodeTestExecutions = (
  node: EnrichedLineageNode,
  testResultsData?: Record<string, TestResult[]>,
) => {
  if (node.testExecutions !== undefined) return node.testExecutions;

  const nodeTests = node.parent
    ? testResultsData?.[node.parent]?.filter(
        (test) => test.metadata.column_name === node.label,
      )
    : testResultsData?.[node.id];

  return nodeTests?.map((t) => ({
    testName: t.metadata.test_name,
    columnName: t.metadata.column_name,
    executionStatus: t.metadata.latest_run_status,
  }));
};

export const getNodeTotals = (
  n: EnrichedLineageNode,
  filters?: UrlFilters,
  testResultsData?: Record<string, TestResult[]>,
  resultsTotalsData?: Record<string, Totals>,
) => {
  const testFilter = filters?.filter((f) => f.property === 'test');
  const statusFilter = filters?.filter((f) => f.property === 'status');

  const filteredTotals: Record<string, number> = {
    passed: 0,
    warnings: 0,
    errors: 0,
  };

  // SaaS (API) nodes carry their own test executions, so node totals are
  // derived from them directly. OSS has no per-node `testExecutions`, so it
  // still gates on the report's per-node totals (`test_results_totals`).
  if (
    n.testExecutions === undefined &&
    !resultsTotalsData?.[n.parent || n.id]
  ) {
    return filteredTotals;
  }

  const nodeTests = getNodeTestExecutions(n, testResultsData);

  const filteredTests = testFilter?.length
    ? nodeTests?.filter((test) =>
        testFilter.every((f) => {
          const testName = test.testName.toLowerCase();
          const filterValue = f.value.map((v) => v.toLowerCase());
          return evaluateStringFilter(testName, filterValue, f.operator);
        }),
      )
    : nodeTests;

  filteredTests?.forEach((test) => {
    switch (test.executionStatus) {
      case 'pass':
        filteredTotals.passed += 1;
        break;
      case 'warn':
        filteredTotals.warnings += 1;
        break;
      case 'fail':
      case 'error':
        filteredTotals.errors += 1;
        break;
      default:
        break;
    }
  });

  if (statusFilter?.length) {
    const statusKeys = Object.keys(filteredTotals);

    const filteredKeys = statusKeys.filter((statusKey) =>
      statusFilter.every((f) =>
        evaluateStringFilter(statusKey, f.value, f.operator),
      ),
    );

    statusKeys.forEach((k) => {
      filteredTotals[k] = filteredKeys.includes(k) ? filteredTotals[k] : 0;
    });
  }

  return filteredTotals;
};

const statusFilterMatch = (
  node: EnrichedLineageNode,
  filters: UrlFilters,
  testResultsData?: Record<string, TestResult[]>,
  resultsTotalsData?: Record<string, Totals>,
): boolean => {
  const statusFilter = filters?.filter((f) => f.property === 'status');
  if (!statusFilter || statusFilter?.length === 0) return true;

  const totals = getNodeTotals(
    node,
    filters,
    testResultsData,
    resultsTotalsData,
  );

  return statusFilter.every((f) => {
    const filterValue = f.value.map((v) => v.toLowerCase());
    switch (f.operator) {
      case 'contains':
      case 'notContains': {
        const keys = Object.keys(totals).map((k) => k.toLowerCase());
        const matchingKeys = keys.filter((k) => k.includes(filterValue[0]));
        const isContains = matchingKeys.some((k) => totals[k] > 0);
        return f.operator === 'contains' ? isContains : !isContains;
      }
      case 'equals':
        return filterValue.some((status) => totals[status] > 0);
      case 'notEquals':
        return !filterValue.some((status) => totals[status] > 0);
      default:
        return false;
    }
  });
};

const filterMatch = (
  node: EnrichedLineageNode,
  filters: UrlFilters,
  nodeKey: NodeFilterKey,
  filterKey: string,
) => {
  const filter = filters?.filter((f) => f.property === filterKey);
  if (!filter || filter.length === 0) return true;

  return filter.every((f) => {
    const filterValue = f.value.map((v) => v.toLowerCase());
    const nodeValue = node[nodeKey];

    const nodeValueFixed = Array.isArray(nodeValue)
      ? (nodeValue || [])
          ?.filter((v) => typeof v === 'string')
          .map((v) => v.toLowerCase())
      : nodeValue && typeof nodeValue === 'string'
      ? [nodeValue.toLowerCase()]
      : [];

    switch (f.operator) {
      case 'equals':
        return filterValue.some((v) => nodeValueFixed.includes(v));
      case 'notEquals':
        return !filterValue.some((v) => nodeValueFixed.includes(v));
      case 'contains':
        return nodeValueFixed.some((v) =>
          filterValue.some((fv) => v.includes(fv)),
        );
      case 'notContains':
        return !nodeValueFixed.some((v) =>
          filterValue.some((fv) => v.includes(fv)),
        );
      default:
        return false;
    }
  });
};

const filterKeys: {
  nodeKey: NodeFilterKey;
  filterKey: string;
}[] = [
  { nodeKey: 'job', filterKey: 'job' },
  { nodeKey: 'tests', filterKey: 'test' },
  { nodeKey: 'tags', filterKey: 'tag' },
  { nodeKey: 'owners', filterKey: 'owner' },
  { nodeKey: 'databaseName', filterKey: 'databaseName' },
  { nodeKey: 'schemaName', filterKey: 'schemaName' },
  { nodeKey: 'tableName', filterKey: 'tableName' },
  { nodeKey: 'materialization', filterKey: 'materialization' },
  { nodeKey: 'path', filterKey: 'path' },
  { nodeKey: 'nodeType', filterKey: 'nodeType' },
  { nodeKey: 'subType', filterKey: 'biNodeType' },
];

const getRootNodes = (
  nodePool: EnrichedLineageNode[],
  filters?: UrlFilters,
  testResultsData?: Record<string, TestResult[]>,
  resultsTotalsData?: Record<string, Totals>,
) => {
  if (!filters || filters.length === 0) {
    return { positiveRoots: undefined, negativeRoots: undefined };
  }

  const positiveFilters = filters.filter(
    (f) => f.operator === 'contains' || f.operator === 'equals',
  );

  const negativeFilters = filters.filter(
    (f) => f.operator === 'notContains' || f.operator === 'notEquals',
  );

  const positiveRoots = positiveFilters.length
    ? nodePool.filter(
        (n) =>
          statusFilterMatch(
            n,
            positiveFilters,
            testResultsData,
            resultsTotalsData,
          ) &&
          filterKeys.every((f) =>
            filterMatch(n, positiveFilters, f.nodeKey, f.filterKey),
          ),
      )
    : undefined;

  const negativeRoots = negativeFilters.length
    ? nodePool.filter(
        (n) =>
          statusFilterMatch(
            n,
            negativeFilters,
            testResultsData,
            resultsTotalsData,
          ) &&
          filterKeys.every((f) =>
            filterMatch(n, negativeFilters, f.nodeKey, f.filterKey),
          ),
      )
    : undefined;

  return { positiveRoots, negativeRoots };
};

// Root nodes are the intersection of the tree selection and the nodes that
// satisfy the filters, so filters can only reduce the set of roots.
const computeRootNodes = (
  selectedEnrichedNodes: EnrichedLineageNode[],
  {
    positiveRoots,
    negativeRoots,
  }: {
    positiveRoots?: EnrichedLineageNode[];
    negativeRoots?: EnrichedLineageNode[];
  },
  hasFilters: boolean,
): EnrichedLineageNode[] => {
  if (!hasFilters) return distinctArrayBy(selectedEnrichedNodes, 'id');

  const joinedRoots =
    positiveRoots && negativeRoots
      ? intersectArraysBy(positiveRoots, negativeRoots, 'id')
      : positiveRoots || negativeRoots || [];

  return distinctArrayBy(
    intersectArraysBy(selectedEnrichedNodes, joinedRoots, 'id'),
    'id',
  );
};

export const rebuildGraphLayout = (
  workerData: WorkerData,
  data: RebuildGraphLayoutParams,
): RebuildGraphLayoutResult | undefined => {
  if (!data.selectedNodes?.length || !workerData.enrichedLineageData) {
    return undefined;
  }

  // Reuse the filter/report context from the last getGraph so incremental
  // rebuilds keep active filters without re-sending them.
  const { filters, reportData } = workerData;

  const selectedEnrichedNodes = data.selectedNodes
    .map((nodeId) =>
      workerData.enrichedLineageData?.nodes.find((node) => node.id === nodeId),
    )
    .filter((node) => !!node) as EnrichedLineageNode[];

  const isChildNodeSelected = selectedEnrichedNodes.some(
    (node) => !!node.parent,
  );

  const nodesOnPath =
    data.isApiLineage && !isChildNodeSelected
      ? workerData.enrichedLineageData?.nodes
      : getNodesOnPath(workerData, selectedEnrichedNodes, undefined, true);

  const { positiveRoots, negativeRoots } = getRootNodes(
    nodesOnPath,
    filters,
    reportData?.test_results,
    reportData?.test_results_totals,
  );

  const hasFilters = !!filters?.length;

  const rootNodes = computeRootNodes(
    selectedEnrichedNodes,
    { positiveRoots, negativeRoots },
    hasFilters,
  );

  const graphNodes = getGraphNodes(
    workerData,
    rootNodes,
    hasFilters,
    nodesOnPath,
  );

  const dagGraph = GraphUtil.functional.subset(workerData.graph!, graphNodes);

  const trimmedGraphNodes = data.isApiLineage
    ? graphNodes
    : applyHopLimit(workerData, dagGraph, selectedEnrichedNodes, {
        upstream: 1,
        downstream: 1,
      });

  workerData.filteredGraph = GraphUtil.functional.subset(
    dagGraph,
    trimmedGraphNodes,
  );

  const reactFlowGraph = getReactFlowGraph(
    workerData,
    rootNodes,
    getNodeTotals,
    data.reactFlowGraph,
    data.lockPosNodes,
    data.highlightedNode,
    data.expandNodes,
    data.collapseNodes,
    data.selectedNodes,
    reportData,
    filters,
    data.onCycleDetected,
  );

  const graphWithHighlightedEdges = highlightNode(
    workerData,
    data.highlightedNode,
    reactFlowGraph,
  );

  return {
    reactFlowGraph: hideAndShiftGraphEdges(
      workerData,
      graphWithHighlightedEdges,
      data.selectedNodes,
      data.highlightedNode,
    ),
    rootNodes,
  };
};

export const computeGetGraphResult = (
  workerData: WorkerData,
  data: GetGraphOnMessageData,
): PostMessageData | undefined => {
  if (!data.selectedNodes?.length) {
    return undefined;
  }
  if (!data.apiLineage && !data.reportData?.lineage) {
    return undefined;
  }

  // Persist filter/report context so incremental rebuilds keep the same context.
  workerData.filters = data.filters;
  workerData.reportData = data.reportData;

  const didRebuildEnrichedData =
    !workerData.enrichedLineageData || !!data.apiLineage;

  if (!workerData.enrichedLineageData || data.apiLineage) {
    workerData.enrichedLineageData = getEnrichedLineageData(
      data.reportData,
      data.excludeColumnNodes,
      data.apiLineage,
    );
  }

  if (!workerData.graph || data.apiLineage) {
    workerData.graph = initGraph(workerData);
  }

  // Restore lazy-loaded columns dropped by the rebuild (assets-only payload
  // excludes them).
  if (didRebuildEnrichedData) {
    reapplyLazyLoadedColumns(workerData);
  }

  if (data.highlightColumnSubgraph) {
    mergeHighlightColumnSubgraphIntoWorkerData(
      workerData,
      data.highlightColumnSubgraph,
    );
  } else {
    // Clear any active highlight subset — unhighlighting routes through
    // getGraph, and the subset is only re-applied via setHighlightColumnSubgraph.
    workerData.highlightColumnSubgraph = undefined;
  }

  debugLog('*********** handleGetGraphMessage start ***********');
  let totalStart = performance.now();
  let start = performance.now();

  const selectedEnrichedNodes = data.selectedNodes
    .map((n) => workerData.enrichedLineageData?.nodes.find((en) => en.id === n))
    .filter((n) => !!n) as EnrichedLineageNode[];

  debugLog('selectedEnrichedNodes: ', performance.now() - start);
  start = performance.now();

  const isChildNodeSelected = selectedEnrichedNodes.some((n) => !!n.parent);

  const nodesOnPath =
    data.apiLineage && !isChildNodeSelected
      ? workerData.enrichedLineageData?.nodes
      : getNodesOnPath(workerData, selectedEnrichedNodes, undefined, true);

  debugLog('nodesOnPath: ', performance.now() - start);
  start = performance.now();

  const { positiveRoots, negativeRoots } = getRootNodes(
    nodesOnPath,
    data.filters,
    data.reportData?.test_results,
    data.reportData?.test_results_totals,
  );

  debugLog('getRootNodes: ', performance.now() - start);
  start = performance.now();

  const hasFilters = !!data.filters?.length;

  const rootNodes = computeRootNodes(
    selectedEnrichedNodes,
    { positiveRoots, negativeRoots },
    hasFilters,
  );

  const graphNodes: EnrichedLineageNode[] = getGraphNodes(
    workerData,
    rootNodes,
    hasFilters,
    nodesOnPath,
  );

  debugLog('graphNodes: ', performance.now() - start);
  start = performance.now();

  const dagGraph = GraphUtil.functional.subset(workerData.graph, graphNodes);

  debugLog('dagGraph: ', performance.now() - start);
  start = performance.now();

  const dagDepthBoundary = data.apiLineage
    ? undefined
    : getDagDepthBoundary(workerData, selectedEnrichedNodes, dagGraph);

  debugLog('dagDepthBoundary: ', performance.now() - start);
  start = performance.now();

  const trimmedGraphNodes = data.apiLineage
    ? graphNodes
    : applyHopLimit(
        workerData,
        dagGraph,
        selectedEnrichedNodes,
        data.hopLimit || {
          upstream: dagDepthBoundary?.incomingDepth ? 1 : 0,
          downstream: dagDepthBoundary?.outgoingDepth ? 1 : 0,
        },
      );

  debugLog('trimmedGraphNodes: ', performance.now() - start);
  start = performance.now();

  workerData.filteredGraph = GraphUtil.functional.subset(
    dagGraph,
    trimmedGraphNodes,
  );

  debugLog('filteredGraph: ', performance.now() - start);
  start = performance.now();

  const renderedEdges = buildRenderedEdgesFromFilteredGraph(
    workerData.filteredGraph,
  );

  if (!data.forceLoadLargeGraph) {
    const isExceeded = checkGraphSizeLimitExceeded(
      trimmedGraphNodes,
      renderedEdges.length,
    );
    if (isExceeded) {
      debugLog(
        `Lineage graph exceeds maximum allowed size. This may affect browser performance.`,
      );
      return { type: 'largeGraphWarning' };
    }
  } else {
    debugLog('Large graph load forced by user despite size limit.');
  }

  debugLog('Size limit check: ', performance.now() - start);
  start = performance.now();

  let cycleNodes: string[] | undefined;
  const reactFlowGraph = getReactFlowGraph(
    workerData,
    rootNodes,
    getNodeTotals,
    data.reactFlowGraph,
    data.lockPosNodes,
    data.highlightedNode,
    data.expandNodes,
    data.collapseNodes,
    data.selectedNodes,
    data.reportData,
    data.filters,
    (detectedCycleNodes) => {
      cycleNodes = detectedCycleNodes;
    },
    renderedEdges,
  );

  debugLog('reactFlowGraph: ', performance.now() - start);
  start = performance.now();

  const graphWithHighlightedEdges = highlightNode(
    workerData,
    data.highlightedNode,
    reactFlowGraph,
  );

  debugLog('graphWithHighlightedEdges: ', performance.now() - start);
  start = performance.now();

  const graphWithShiftedEdges = hideAndShiftGraphEdges(
    workerData,
    graphWithHighlightedEdges,
    data.selectedNodes,
    data.highlightedNode,
  );

  debugLog('graphWithShiftedEdges: ', performance.now() - start);
  start = performance.now();

  const upstreamNodes = data.calcDownstreamUpstream
    ? getStreamNodes(workerData, 'upstream', undefined, rootNodes, false)
    : undefined;

  debugLog('upstreamNodes: ', performance.now() - start);
  start = performance.now();

  const downstreamNodes = data.calcDownstreamUpstream
    ? getStreamNodes(workerData, 'downstream', undefined, rootNodes, false)
    : undefined;

  debugLog('downstreamNodes: ', performance.now() - start);
  debugLog(
    '*********** handleGetGraphMessage end *********** ',
    performance.now() - totalStart,
  );

  return {
    type: 'getGraph',
    data: {
      rootNodes,
      nodes: trimmedGraphNodes,
      edges: workerData.enrichedLineageData?.edges ?? [],
      reactFlowGraph: graphWithShiftedEdges,
      upstreamNodes,
      downstreamNodes,
      dagDepthBoundary,
      cycleNodes,
    },
  };
};
